import { getDB, reqDone, txDone, STORES, type AgentPromptOutboxRecord } from "./idb.js";
import { reportUiFailure } from "../report-failure.js";

export type { AgentPromptOutboxRecord };

// A runtime with NO IndexedDB at all (a private window that blocks it, SSR) is a
// no-op, like the reader and the delete below: the send still goes and only
// reload-time reconciliation is lost. Refusing instead left the composer failing
// every message with a Retry that could never succeed. A write that FAILS still
// throws — that is a fault, not a missing feature.
export async function putAgentPromptOutbox(record: AgentPromptOutboxRecord): Promise<void> {
  const dbp = getDB();
  if (!dbp) return;
  const db = await dbp;
  const tx = db.transaction(STORES.AGENT_PROMPT_OUTBOX, "readwrite");
  const store = tx.objectStore(STORES.AGENT_PROMPT_OUTBOX);
  store.put(record);
  // These are unresolved sends, not replaceable cache entries. Only a correlated
  // OUTCOME — an echo, a verdict, a proven refusal of the send or of the
  // conversation's read (retireRefusedAgentPrompts) — or the reader's retirement
  // bound below may remove them. Never counted, never evicted to make room.
  await txDone(tx);
}

export async function deleteAgentPromptOutbox(clientMessageId: string): Promise<void> {
  const dbp = getDB();
  if (!dbp) return;
  try {
    const db = await dbp;
    const tx = db.transaction(STORES.AGENT_PROMPT_OUTBOX, "readwrite");
    tx.objectStore(STORES.AGENT_PROMPT_OUTBOX).delete(clientMessageId);
    await txDone(tx);
  } catch {
    // Best-effort cleanup. A stale row is deduped by clientMessageId on hydrate.
  }
}

// How long an unresolved send may keep asking to be resolved. Every way one can settle
// gets its chance on the first load after the send and none grows likelier with time, so
// the bound is the PERSON's window to notice it and act: a week, then it is retired.
export const PROMPT_OUTBOX_RETIREMENT_MS = 7 * 24 * 60 * 60 * 1000;

export async function getAgentPromptOutbox(
  owner: string,
  workspaceId: string,
  now: number = Date.now(),
): Promise<AgentPromptOutboxRecord[]> {
  const dbp = getDB();
  if (!dbp) return [];
  try {
    const db = await dbp;
    const tx = db.transaction(STORES.AGENT_PROMPT_OUTBOX, "readonly");
    const all =
      ((await reqDone(
        tx.objectStore(STORES.AGENT_PROMPT_OUTBOX).getAll(),
      )) as AgentPromptOutboxRecord[]) ?? [];
    const live: AgentPromptOutboxRecord[] = [];
    for (const item of all) {
      if (item.owner !== owner || item.workspaceId !== workspaceId) continue;
      // Retired, not hidden: this read is the only thing that ever visits the record.
      if (now - item.createdAt > PROMPT_OUTBOX_RETIREMENT_MS) {
        void deleteAgentPromptOutbox(item.clientMessageId);
        continue;
      }
      live.push(item);
    }
    return live.sort((a, b) => a.createdAt - b.createdAt);
  } catch (err) {
    // A failed read is not "no queued prompts": they would vanish from the composer.
    reportUiFailure("agent_prompt_outbox.read", err);
    return [];
  }
}

/**
 * The words of a held prompt its author edited: a reload restores the bubble from this row, so it
 * must carry the daemon's current text. A row already reconciled away is not resurrected.
 */
export async function updateAgentPromptOutboxText(
  clientMessageId: string,
  text: string,
): Promise<void> {
  await patchAgentPromptOutbox(clientMessageId, { text }, "agent_prompt_outbox.update_text");
}

// Held by the daemon: a reload restores it into the queue bar only (Fab 2026-10-21).
export async function markAgentPromptOutboxQueued(clientMessageId: string): Promise<void> {
  await patchAgentPromptOutbox(
    clientMessageId,
    { queued: true },
    "agent_prompt_outbox.mark_queued",
  );
}

async function patchAgentPromptOutbox(
  clientMessageId: string,
  patch: Partial<AgentPromptOutboxRecord>,
  failureSite: string,
): Promise<void> {
  const dbp = getDB();
  if (!dbp) return;
  try {
    const db = await dbp;
    const tx = db.transaction(STORES.AGENT_PROMPT_OUTBOX, "readwrite");
    const store = tx.objectStore(STORES.AGENT_PROMPT_OUTBOX);
    const existing = (await reqDone(store.get(clientMessageId))) as
      | AgentPromptOutboxRecord
      | undefined;
    if (existing) store.put({ ...existing, ...patch });
    await txDone(tx);
  } catch (err) {
    reportUiFailure(failureSite, err);
  }
}

/**
 * Stamp the relay's minted attempt onto a row already written. The row is written
 * BEFORE the send (that is what makes it durable), so the id only exists once the
 * ack comes back. A row that has since been reconciled away is not resurrected.
 */
export async function stampAgentPromptAttempt(
  clientMessageId: string,
  promptAttemptId: string,
): Promise<void> {
  const dbp = getDB();
  if (!dbp) return;
  try {
    const db = await dbp;
    const tx = db.transaction(STORES.AGENT_PROMPT_OUTBOX, "readwrite");
    const store = tx.objectStore(STORES.AGENT_PROMPT_OUTBOX);
    const existing = (await reqDone(store.get(clientMessageId))) as
      | AgentPromptOutboxRecord
      | undefined;
    if (existing) store.put({ ...existing, promptAttemptId });
    await txDone(tx);
  } catch {
    // Best-effort: without the stamp a reload reconciles the way it did before.
  }
}
